import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';

// A more robust StatusBadge component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
    approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
    purchased: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
    delivered: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
  };
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.replace('_', ' ')}
    </span>
  );
};

// Stat card component
const StatCard = ({ label, value, colorClass }) => (
    <div className={`p-4 rounded-lg shadow ${colorClass}`}>
        <p className="text-sm font-medium opacity-75">{label}</p>
        <p className="text-3xl font-bold">{value}</p>
    </div>
);

export default function RecipientHistoryPage() {
    const { userId } = useParams();
    const { token } = useAuth();
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchHistory = async () => {
            if (!token || !userId) return;
            setLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${userId}/history`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('Could not fetch recipient history.');
                const data = await response.json();
                setHistory(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchHistory();
    }, [token, userId]);

    if (loading) return <p className="p-8 text-center">Loading recipient history...</p>;
    if (!history) return <p className="p-8 text-center text-red-500">No history found for this user.</p>;
    
    const { user, campaigns } = history;

    // ✅ THE FIX: Calculate counts for all statuses
    const stats = campaigns.reduce((acc, c) => {
        acc[c.status] = (acc[c.status] || 0) + 1;
        return acc;
    }, {});
    
    const totalCampaigns = campaigns.length;

    return (
        <div className="space-y-8">
            <Link to="/admin/users" className="inline-flex items-center text-orange-500 hover:underline">
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back to Manage Users
            </Link>
            
            <div>
                <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">{user.first_name} {user.last_name}'s History</h1>
                <p className="text-lg text-gray-500 dark:text-gray-400">{user.email}</p>
            </div>
            
            {/* ✅ THE FIX: Display all relevant stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard label="Total Requests" value={totalCampaigns} colorClass="bg-gray-200 dark:bg-gray-700" />
                <StatCard label="Pending" value={stats.pending || 0} colorClass="bg-yellow-200 dark:bg-yellow-800" />
                <StatCard label="Approved" value={stats.approved || 0} colorClass="bg-blue-200 dark:bg-blue-800" />
                <StatCard label="Purchased" value={stats.purchased || 0} colorClass="bg-green-200 dark:bg-green-800" />
                <StatCard label="Rejected" value={stats.rejected || 0} colorClass="bg-red-200 dark:bg-red-800" />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="p-4">Product</th>
                            <th className="p-4">Date Requested</th>
                            <th className="p-4">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                        {campaigns.map(c => (
                            <tr key={c.id}>
                                <td className="p-4 font-medium">{c.product_name}</td>
                                <td className="p-4 text-gray-500">{new Date(c.created_at).toLocaleDateString()}</td>
                                <td className="p-4"><StatusBadge status={c.status} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}